import { Link } from 'react-router-dom';
import { Target, BarChart3, Layers, Compass } from 'lucide-react';

export default function BlogPolymarketStrategies() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <header className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800 border border-primary-500/40 text-xs text-primary-200 uppercase tracking-wide">
            <Compass className="w-3 h-3" />
            <span>Polymarket Copy Trading Strategies</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            Three Polymarket Copy Trading Setups You Can Actually Run With BetMimic
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Copy trading only works when you treat it like a strategy, not a slot machine. Here are three
            concrete ways to use BetMimic on top of Polymarket – from conservative &quot;satellite&quot; exposure
            to full‑time degen mirroring – with sizing rules and risk limits that won&apos;t nuke your stack.
          </p>
        </header>

        {/* Strategy 1: Conservative satellite book */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            Strategy #1 – Conservative &quot;satellite&quot; copy book
          </h2>
          <p className="text-sm text-dark-300">
            You already have a main book – crypto, sports, macro, whatever. You want Polymarket exposure
            without babysitting markets. The goal is small, uncorrelated convexity, not max leverage.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Copy 1–2 long‑term traders with clear edge (high volume, stable PnL) using{' '}
              <span className="font-medium">fixed dollar</span> sizing.
            </li>
            <li>
              Set low <span className="font-medium">max exposure</span> per config – e.g. $250–$500 total
              open risk per trader.
            </li>
            <li>
              Turn on <span className="font-medium">stop loss</span> at ~25–35% and
              <span className="font-medium">take profit</span> at 50–100%.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            This gives you a slow‑burn prediction market sleeve that won&apos;t hijack your main portfolio if
            one trader has a cold month.
          </p>
        </section>

        {/* Strategy 2: Aggressive trader mirroring */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary-400" />
            Strategy #2 – Aggressive trader mirroring with dynamic size
          </h2>
          <p className="text-sm text-dark-300">
            Here you&apos;re effectively turning a single Polymarket wallet into a &quot;managed account&quot; you
            ride aggressively, with tighter risk rails.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Use <span className="font-medium">percentage of trader size</span> for sizing so your notional
              moves in sync with their conviction.
            </li>
            <li>
              Set a meaningful <span className="font-medium">daily loss limit</span> – when their copied PnL
              hits that drawdown, BetMimic automatically stops copying for the rest of the day.
            </li>
            <li>
              Cap <span className="font-medium">max open positions</span> to avoid getting scattered across
              40 micro‑markets with no real edge.
            </li>
          </ul>
          <p className="text-xs text-dark-500">
            This setup makes sense when you&apos;ve watched a trader for a while and want real upside
            participation without giving them infinite rope over your bankroll.
          </p>
        </section>

        {/* Strategy 3: Basket of traders */}
        <section className="card space-y-3">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary-400" />
            Strategy #3 – Basket of traders, one engine
          </h2>
          <p className="text-sm text-dark-300">
            Instead of going all‑in on one &quot;hero&quot; account, you can run a diversified basket of Polymarket
            traders, each with smaller risk, all managed by the same BetMimic engine.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
            <li>
              Create multiple <span className="font-medium">copy configs</span> with different sizing modes
              and filters for each trader.
            </li>
            <li>
              Stagger <span className="font-medium">max exposure</span> and daily trade caps per config so no
              single trader can dominate your risk.
            </li>
            <li>
              Use filters to specialize traders – e.g. one for long‑dated politics, one for sports, one
              for event‑driven crypto markets.
            </li>
          </ul>
        </section>

        {/* CTA */}
        <section className="space-y-3">
          <p className="text-sm text-dark-300">
            The point of BetMimic is not just to &quot;copy good traders&quot; – it&apos;s to turn Polymarket wallets
            into programmable strategies that live inside your own risk box. Once you know what outcome
            you want – slow satellite book, aggressive mirroring, or diversified basket – the copy configs
            and risk fields in the app map cleanly onto it.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/copy-configs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
            >
              <Target className="w-4 h-4" />
              Build your first Polymarket copy strategy
            </Link>
            <Link
              to="/traders"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              Find traders to add to your basket
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
